// GPT-5 Orchestration and Tasking Agent
// Coordinates GPT-4 Mini Web Search and Deep Research APIs
import { blink } from '@/lib/blink';
import { safeJsonParse } from '@/lib/utils';



// Get current AI provider preferences
function getAiPrefs() {
  try {
    const provider = (typeof window !== 'undefined' && localStorage.getItem('ai_provider')) || 'openai';
    const model = (typeof window !== 'undefined' && localStorage.getItem('ai_model')) || 'gpt-5';
    return { provider, model };
  } catch {
    return { provider: 'openai', model: 'gpt-5' };
  }
}

interface Task {
  id: string;
  type: 'web_search' | 'deep_research' | 'analysis' | 'generation';
  prompt: string;
  dependencies?: string[];
  status: 'pending' | 'running' | 'completed' | 'failed';
  result?: any;
}

interface OrchestrationPlan {
  tasks: Task[];
  executionOrder: string[][];
}

/**
 * GPT-5 Orchestrator
 * Coordinates complex multi-step AI operations using GPT-5's advanced reasoning
 */
export class GPT5Orchestrator {
  private tasks: Map<string, Task> = new Map();
  private completedTasks: Set<string> = new Set();

  /**
   * Plan execution strategy using GPT-5
   * Handles token limits and query complexity intelligently
   */
  async planExecution(userPrompt: string, maxComplexity: number = 5): Promise<OrchestrationPlan> {
    const { provider, model } = getAiPrefs();

    // Truncate very long prompts to prevent token overflow
    const truncatedPrompt = userPrompt.length > 4000 
      ? userPrompt.substring(0, 4000) + '... [truncated for processing]'
      : userPrompt;

    // Use GPT-5 to decompose complex requests into subtasks
    const planningPrompt = `You are a GPT-5 orchestration agent. Break down this request into specific subtasks (max ${maxComplexity} tasks):

Request: ${truncatedPrompt}

Available tools:
1. GPT-4-mini-web-search: Fast web search with real-time information
2. Deep-research: Comprehensive research with multiple sources and synthesis
3. Analysis: Structured analysis of data or content
4. Generation: Create content, drafts, or responses

Provide a JSON plan with tasks array. Each task should have:
- id: unique identifier
- type: one of the available tools
- prompt: specific prompt for that task
- dependencies: array of task IDs that must complete first (or empty array)

Example:
{
  "tasks": [
    {"id": "search1", "type": "web_search", "prompt": "Find latest info on...", "dependencies": []},
    {"id": "research1", "type": "deep_research", "prompt": "Research X using findings from search1", "dependencies": ["search1"]},
    {"id": "generate1", "type": "generation", "prompt": "Create summary based on research1", "dependencies": ["research1"]}
  ]
}

Respond with ONLY the JSON plan, no other text.`;

    try {
      // Use adaptive token limit based on prompt length
      const planningTokens = Math.min(2000, Math.max(800, Math.floor(truncatedPrompt.length / 3)));
      const response = await this.callGPT5(planningPrompt, planningTokens);
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No JSON plan found in response');
      }
      
      const plan = safeJsonParse(jsonMatch[0]);
      
      // Validate and create tasks (limit to maxComplexity)
      const tasks: Task[] = plan.tasks.slice(0, maxComplexity).map((t: any) => ({
        ...t,
        status: 'pending' as const,
        dependencies: t.dependencies || []
      }));

      // Calculate execution order (topological sort)
      const executionOrder = this.calculateExecutionOrder(tasks);

      return { tasks, executionOrder };
    } catch (error) {
      console.error('Planning failed, using single-step fallback:', error);
      // Fallback to single task
      return {
        tasks: [{
          id: 'task1',
          type: 'web_search',
          prompt: userPrompt,
          dependencies: [],
          status: 'pending'
        }],
        executionOrder: [['task1']]
      };
    }
  }

  /**
   * Execute orchestrated plan with timeout protection
   */
  async execute(plan: OrchestrationPlan, onProgress?: (task: Task) => void, timeout: number = 180000): Promise<string> {
    this.tasks.clear();
    this.completedTasks.clear();

    // Initialize tasks
    plan.tasks.forEach(task => {
      this.tasks.set(task.id, task);
    });

    // Execute in order with timeout protection
    const executionPromise = (async () => {
      for (const batch of plan.executionOrder) {
        // Execute batch in parallel
        await Promise.all(
          batch.map(taskId => this.executeTask(taskId, onProgress))
        );
      }
    })();

    // Add timeout
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Execution timeout')), timeout)
    );

    try {
      await Promise.race([executionPromise, timeoutPromise]);
    } catch (error) {
      console.error('Execution error or timeout:', error);
      // Continue to synthesis with whatever was completed
    }

    // Synthesize final result using GPT-5
    return await this.synthesizeResults();
  }

  /**
   * Execute a single task
   */
  private async executeTask(taskId: string, onProgress?: (task: Task) => void): Promise<void> {
    const task = this.tasks.get(taskId);
    if (!task) return;

    task.status = 'running';
    onProgress?.(task);

    try {
      switch (task.type) {
        case 'web_search':
          task.result = await this.webSearch(task.prompt);
          break;
        case 'deep_research':
          task.result = await this.deepResearch(task.prompt, task.dependencies);
          break;
        case 'analysis':
          task.result = await this.analyze(task.prompt, task.dependencies);
          break;
        case 'generation':
          task.result = await this.generate(task.prompt, task.dependencies);
          break;
      }
      
      task.status = 'completed';
      this.completedTasks.add(taskId);
      onProgress?.(task);
    } catch (error) {
      task.status = 'failed';
      task.result = { error: String(error) };
      onProgress?.(task);
    }
  }

  /**
   * GPT-4 Mini Web Search
   * Fast, real-time web search capabilities
   */
  private async webSearch(query: string): Promise<any> {
    try {
      // Use Blink's web search capability
      const searchResults = await blink.data.search(query, {
        limit: 10
      });

      // Use GPT-4-mini to process and summarize results
      const summaryPrompt = `Analyze these web search results and provide a concise summary:

Query: ${query}

Results:
${JSON.stringify(searchResults.organic_results?.slice(0, 5) || [], null, 2)}

Provide a clear, factual summary with key findings.`;

      const summary = await this.callGPT4Mini(summaryPrompt, 800);

      return {
        query,
        results: searchResults.organic_results || [],
        related_searches: searchResults.related_searches || [],
        summary
      };
    } catch (error) {
      console.error('Web search failed:', error);
      return { query, error: String(error), results: [] };
    }
  }

  /**
   * Deep Research using OpenAI's Deep Research API patterns
   * Multi-step research with source synthesis and structured output
   */
  private async deepResearch(query: string, dependencies: string[]): Promise<any> {
    try {
      // Import deep research service
      const { deepResearch: deepResearchService } = await import('./deepResearch');
      
      // Gather context from dependencies
      const context = this.gatherDependencyContext(dependencies, 2000);
      
      // Construct enhanced query with context
      const enhancedQuery = context 
        ? `${query}\n\nContext from previous tasks:\n${context}`
        : query;
      
      // Use deep research service with full model for comprehensive analysis
      const result = await deepResearchService.research({
        query: enhancedQuery,
        useFullModel: true,
        outputFormat: 'structured',
        requireCitations: true
      });
      
      // Get additional web sources for cross-reference
      const webResults = await blink.data.search(query, { limit: 15 });
      
      return {
        query,
        research: result.report,
        citations: result.citations,
        metadata: result.metadata,
        additional_sources: webResults.organic_results || [],
        context: context || 'None'
      };
    } catch (error) {
      console.error('Deep research failed, using fallback:', error);
      
      // Fallback to original web search + synthesis approach
      const context = this.gatherDependencyContext(dependencies, 2000);
      const truncatedQuery = query.length > 1000 ? query.substring(0, 1000) + '...' : query;

      const researchPrompt = `Conduct deep research on this topic using multiple perspectives:

Topic: ${truncatedQuery}

${context ? `Context from previous tasks:\n${context}\n` : ''}

Provide:
1. Key findings from multiple authoritative sources
2. Different perspectives and viewpoints
3. Data and statistics when available
4. Synthesis of information
5. Gaps in current knowledge

Use your reasoning capabilities to provide comprehensive research.`;

      const researchTokens = Math.min(4000, Math.max(1500, 2000 + Math.floor(truncatedQuery.length / 2)));
      const research = await this.callGPT5(researchPrompt, researchTokens);
      const webResults = await blink.data.search(query, { limit: 15 });

      return {
        query,
        research,
        sources: webResults.organic_results || [],
        context: context || 'None'
      };
    }
  }

  /**
   * Analyze data or content
   */
  private async analyze(prompt: string, dependencies: string[]): Promise<any> {
    const context = this.gatherDependencyContext(dependencies);

    const analysisPrompt = `Perform structured analysis:

${prompt}

${context ? `Data to analyze:\n${context}\n` : ''}

Provide a thorough, structured analysis with clear conclusions.`;

    const analysis = await this.callGPT5(analysisPrompt, 2000);

    return { analysis, sources: dependencies };
  }

  /**
   * Generate content based on research
   */
  private async generate(prompt: string, dependencies: string[]): Promise<any> {
    const context = this.gatherDependencyContext(dependencies);

    const generationPrompt = `Generate content based on the following:

Task: ${prompt}

${context ? `Source material:\n${context}\n` : ''}

Create high-quality, accurate content that synthesizes the information.`;

    const content = await this.callGPT5(generationPrompt, 2500);

    return { content, sources: dependencies };
  }

  /**
   * Synthesize all results into final response
   */
  private async synthesizeResults(): Promise<string> {
    const allResults = Array.from(this.tasks.values())
      .filter(task => task.status === 'completed')
      .map(task => ({
        type: task.type,
        prompt: task.prompt,
        result: task.result
      }));

    const synthesisPrompt = `Synthesize these research and analysis results into a comprehensive, well-structured response:

${JSON.stringify(allResults, null, 2)}

Provide a clear, actionable response that addresses the original request.`;

    return await this.callGPT5(synthesisPrompt, 2000);
  }

  /**
   * Calculate execution order using topological sort
   */
  private calculateExecutionOrder(tasks: Task[]): string[][] {
    const graph = new Map<string, Set<string>>();
    const inDegree = new Map<string, number>();

    // Build graph
    tasks.forEach(task => {
      graph.set(task.id, new Set());
      inDegree.set(task.id, 0);
    });

    tasks.forEach(task => {
      task.dependencies?.forEach(dep => {
        graph.get(dep)?.add(task.id);
        inDegree.set(task.id, (inDegree.get(task.id) || 0) + 1);
      });
    });

    // Topological sort with batching
    const order: string[][] = [];
    const queue: string[] = [];

    // Find tasks with no dependencies
    inDegree.forEach((degree, taskId) => {
      if (degree === 0) queue.push(taskId);
    });

    while (queue.length > 0) {
      // All tasks in current queue can run in parallel
      const batch = [...queue];
      order.push(batch);
      queue.length = 0;

      batch.forEach(taskId => {
        graph.get(taskId)?.forEach(dependentId => {
          const newDegree = (inDegree.get(dependentId) || 0) - 1;
          inDegree.set(dependentId, newDegree);
          if (newDegree === 0) {
            queue.push(dependentId);
          }
        });
      });
    }

    return order;
  }

  /**
   * Gather context from completed dependency tasks with size limit
   */
  private gatherDependencyContext(dependencies?: string[], maxLength: number = 3000): string {
    if (!dependencies || dependencies.length === 0) return '';

    const contexts = dependencies
      .map(depId => {
        const task = this.tasks.get(depId);
        if (!task || task.status !== 'completed') return null;
        
        const resultStr = JSON.stringify(task.result, null, 2);
        // Truncate individual results if too long
        const truncated = resultStr.length > 1000 ? resultStr.substring(0, 1000) + '... [truncated]' : resultStr;
        return `Task ${depId} (${task.type}):\n${truncated}`;
      })
      .filter(Boolean);

    const fullContext = contexts.join('\n\n');
    
    // Truncate total context if exceeds limit
    return fullContext.length > maxLength 
      ? fullContext.substring(0, maxLength) + '\n\n[Context truncated for token limit]'
      : fullContext;
  }

  /**
   * Call GPT-5 for orchestration and synthesis
   * Uses Blink AI native capabilities (no external API key required)
   */
  private async callGPT5(prompt: string, maxTokens: number): Promise<string> {
    try {
      // Use Blink AI's built-in capabilities for all orchestration
      const { text } = await blink.ai.generateText({ prompt, maxTokens });
      return text;
    } catch (error) {
      console.error('GPT-5 call failed:', error);
      throw new Error(`AI processing failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Call GPT-4-mini for fast operations with web search
   * Uses Blink AI with web search enabled
   */
  private async callGPT4Mini(prompt: string, maxTokens: number): Promise<string> {
    try {
      // Use Blink AI with web search for fast, real-time information
      const { text } = await blink.ai.generateText({ prompt, maxTokens, search: true });
      return text;
    } catch (error) {
      console.error('GPT-4-mini call failed:', error);
      throw new Error(`AI processing failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

// Singleton instance
export const gpt5Orchestrator = new GPT5Orchestrator();

/**
 * Streaming orchestration for real-time responses
 */
export async function streamOrchestration({
  prompt,
  onProgress,
  onChunk,
  onComplete
}: {
  prompt: string;
  onProgress?: (stage: string, data: any) => void;
  onChunk?: (chunk: string) => void;
  onComplete?: (result: string) => void;
}) {
  // Stage 1: Planning
  onProgress?.('planning', { message: 'Analyzing request and planning execution...' });
  const plan = await gpt5Orchestrator.planExecution(prompt);
  
  onProgress?.('plan_created', { plan });

  // Stage 2: Execution
  onProgress?.('executing', { message: 'Executing research and analysis tasks...' });
  
  let currentStage = '';
  const result = await gpt5Orchestrator.execute(plan, (task) => {
    const stage = `${task.type}_${task.status}`;
    if (stage !== currentStage) {
      currentStage = stage;
      onProgress?.(stage, { task });
      
      // Stream progress updates
      if (task.status === 'running') {
        onChunk?.(`\n\n🔄 ${task.type}: ${task.prompt}\n`);
      } else if (task.status === 'completed') {
        onChunk?.(`\n✅ Completed: ${task.type}\n`);
      }
    }
  });

  // Stage 3: Final synthesis
  onProgress?.('synthesizing', { message: 'Synthesizing final response...' });
  
  // Stream the final result
  const words = result.split(' ');
  for (const word of words) {
    onChunk?.(word + ' ');
    await new Promise(resolve => setTimeout(resolve, 20));
  }

  onComplete?.(result);
}
